import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { ArrowRight, Map as MapIcon, CalendarDays, List, MapPin } from "lucide-react";
import { Logo } from "../components/ui/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Footer } from "../components/layout/Footer";
import { useViewMode, type ViewMode } from "../contexts/ViewModeContext";
import { useDanceStyles } from "../hooks/useDanceStyles";
import { useCurrentUser } from "../hooks/useCurrentUser";

const VIEW_LINKS: { view: ViewMode; label: string; icon: typeof MapIcon }[] = [
  { view: "map", label: "See the map", icon: MapIcon },
  { view: "calendar", label: "Calendar", icon: CalendarDays },
  { view: "list", label: "Every event", icon: List },
];

/**
 * The front door, built around the one question most visitors arrive with:
 * what is on near me? Everything else — other views, styles, and the asks
 * aimed at studios — sits below that, because nobody submits an event to a
 * directory they haven't looked at yet.
 */
export function LandingPage() {
  const [, navigate] = useLocation();
  const { setViewMode } = useViewMode();
  const { groups, styles } = useDanceStyles();
  const { user } = useCurrentUser();

  const [zip, setZip] = useState("");
  const [zipError, setZipError] = useState(false);

  // A returning, signed-in visitor starts from the zip they last searched.
  useEffect(() => {
    if (user?.lastZipCode) setZip((current) => current || user.lastZipCode!);
  }, [user?.lastZipCode]);

  /**
   * The view is set alongside the navigation, the same way the header does it.
   * ViewModeProvider reads ?view= only when the app first loads, so a link to
   * /events?view=map from here would still open on the list.
   */
  const go = (view: ViewMode, query = "") => {
    navigate(query ? `/events?${query}` : "/events");
    setViewMode(view);
  };

  const handleZipSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{5}$/.test(zip)) {
      setZipError(true);
      return;
    }
    go("nearby", `zip=${zip}`);
  };

  const styleGroups = groups.filter((g) => g !== "Other");

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 sm:py-12">
        {/* Logo */}
        <div className="mb-4 sm:mb-6">
          <Logo className="h-12 sm:h-14 w-auto text-foreground" />
        </div>

        {/* Beta badge */}
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/15 text-primary border border-primary/25 mb-3 sm:mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
          Currently in Beta
        </span>

        {/* Headline */}
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-2 sm:mb-3 tracking-tight">
          Find Social Dances<br className="hidden sm:block" /> Near&nbsp;You
        </h1>

        <p className="text-muted-foreground text-center max-w-md mb-5 sm:mb-8 text-sm sm:text-base leading-relaxed">
          Classes, socials, and workshops across New England.
        </p>

        {/* Zip search — the page's main action */}
        <form onSubmit={handleZipSubmit} className="w-full max-w-md" noValidate>
          <label htmlFor="landing-zip" className="sr-only">
            Your zip code
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                id="landing-zip"
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={5}
                placeholder="Your zip code"
                value={zip}
                onChange={(e) => {
                  setZip(e.target.value.replace(/\D/g, "").slice(0, 5));
                  setZipError(false);
                }}
                aria-invalid={zipError || undefined}
                aria-describedby={zipError ? "landing-zip-error" : undefined}
                className="h-11 pl-9 text-base md:text-base"
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="h-11 gap-2 px-5 has-[>svg]:px-5 text-base font-semibold shadow-lg shadow-primary/25 group"
            >
              Show dances
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
          {zipError && (
            <p id="landing-zip-error" className="mt-2 text-xs text-destructive">
              Enter a 5-digit zip code.
            </p>
          )}
        </form>

        {/* Browse by style */}
        {styleGroups.length > 0 && (
          <div className="w-full max-w-md mt-5 sm:mt-6">
            <p className="text-xs text-muted-foreground text-center mb-2">Or browse by style</p>
            <div className="flex flex-wrap justify-center gap-2">
              {styleGroups.map((group) => {
                const slugs = styles.filter((s) => s.group === group).map((s) => s.slug);
                return (
                  <button
                    key={group}
                    type="button"
                    onClick={() => go("list", `style=${slugs.join(",")}`)}
                    className="px-3.5 py-1.5 rounded-full text-sm border border-border bg-card text-foreground hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
                  >
                    {group}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Other views */}
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-5 sm:mt-6">
          {VIEW_LINKS.map(({ view, label, icon: Icon }) => (
            <button
              key={view}
              type="button"
              onClick={() => go(view)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline underline-offset-4 cursor-pointer"
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* For studios and organizers — below the fold of attention, on purpose */}
        <p className="mt-8 sm:mt-12 text-xs sm:text-sm text-muted-foreground text-center max-w-md">
          Run a class, social, or studio?{" "}
          <Link href="/submit" className="text-foreground font-medium hover:underline underline-offset-4">
            Submit an event
          </Link>{" "}
          or{" "}
          <Link href="/signup" className="text-foreground font-medium hover:underline underline-offset-4">
            create an organizer account
          </Link>
          .
        </p>
      </div>

      <Footer />
    </div>
  );
}
